<template>
  <BasePaneCard
    heading="Perfil demográfico"
    headingTooltip="Dados de todos os leads<br>baseados em safra, período,<br>ação ou origem."
  >
    <template #header-tray>
      <div class="widget-demographic-profile__chips">
        <div
          v-for="chip in chips"
          :key="chip"
          class="chip"
        >
          {{ chip }}
        </div>
      </div>
    </template>

    <div class="widget-demographic-profile">
      <div
        v-if="isLoading"
        class="widget-demographic-profile__loading-container"
      >
        <BaseLoaderDefault />
      </div>

      <BaseGrid
        v-else
        columns="2"
      >
        <template v-if="!indicators.length">
          <BaseEmptyState class="widget-demographic-profile__error-state">
            <SvgIconRobot />

            <h3>Ops! Algo deu errado...</h3>

            <p>Tentar novamente</p>
          </BaseEmptyState>
        </template>

        <template v-else>
          <BaseEmptyState v-if="isIndicatorEmpty(selectedCard)">
            <SvgIconWorld />

            <h3>Não existem dados suficientes<br />para a criação do mapa</h3>

            <p>Selecione outro filtro para ser<br />aplicado à página.</p>
          </BaseEmptyState>

          <BaseInteractiveMap
            v-else
            :geoJson="brazilRegions"
            :selectedCard="selectedCard"
            :indicators="indicators"
            :formattedIndicators="formattedIndicators"
            :hoveredRegion="hoveredRegion"
            class="widget-demographic-profile__map"
            @update:hoveredRegion="hoveredRegion = $event"
          />

          <div class="widget-demographic-profile__cards">
            <div class="widget-demographic-profile__cards-indicators">
              <template v-for="(card, index) in filteredCards">
                <button
                  v-if="!isIndicatorDisabled(card.key) && !isIndicatorEmpty(card.key)"
                  :key="index"
                  type="button"
                  class="widget-demographic-profile__card"
                  :class="{ 'widget-demographic-profile__card--active': isCardActive(card.key) }"
                  @click="selectedCard = card.key"
                >
                  <h4
                    class="widget-demographic-profile__card-title"
                    :title="card.title"
                  >
                    {{ card.title }}
                  </h4>

                  <h4
                    v-if="card.value"
                    :title="
                      formatIndicatorValue(selectedRegionIndicators[card.value], card.valueType)
                    "
                    class="widget-demographic-profile__card-value"
                  >
                    {{ formatIndicatorValue(selectedRegionIndicators[card.value], card.valueType) }}
                  </h4>

                  <p
                    v-if="card.text && selectedRegionIndicators[card.text]"
                    :title="
                      formatIndicatorValue(selectedRegionIndicators[card.text], card.textType)
                    "
                    class="widget-demographic-profile__card-text"
                  >
                    {{ formatIndicatorValue(selectedRegionIndicators[card.text], card.textType) }}
                  </p>
                </button>

                <button
                  v-else-if="isIndicatorEmpty(card.key)"
                  :key="index"
                  class="widget-demographic-profile__card widget-demographic-profile__card--disabled"
                  :class="{ 'widget-demographic-profile__card--active': isCardActive(card.key) }"
                  @click="selectedCard = card.key"
                >
                  <h4
                    class="widget-demographic-profile__card-title"
                    :title="card.title"
                  >
                    {{ card.title }}
                  </h4>

                  <p class="widget-demographic-profile__card-text">Não existem dados suficientes</p>
                </button>
              </template>
            </div>

            <button
              v-if="!isIndicatorEmpty('feeling')"
              class="widget-demographic-profile__cards-feelings widget-demographic-profile__card widget-demographic-profile__card--horizontal"
              :class="{
                'widget-demographic-profile__card--active': isCardActive('feeling')
              }"
              @click="selectedCard = 'feeling'"
            >
              <h2 class="widget-demographic-profile__cards-feelings__title">Sentimento</h2>

              <div class="widget-demographic-profile__cards-feelings__container">
                <div
                  v-for="feeling in feelings"
                  :key="feeling.label"
                  class="widget-demographic-profile__feeling"
                >
                  <Component :is="feeling.icon" />

                  <p class="widget-demographic-profile__card-text">
                    {{ feeling.label }}
                  </p>

                  <h4 class="widget-demographic-profile__card-value">
                    {{ formatIndicatorValue(selectedRegionIndicators[feeling.key], 'percentage') }}
                  </h4>
                </div>
              </div>
            </button>

            <button
              v-else
              class="widget-demographic-profile__card widget-demographic-profile__card--horizontal widget-demographic-profile__card--disabled"
              :class="{
                'widget-demographic-profile__card--active': isCardActive('feeling')
              }"
              @click="selectedCard = 'feeling'"
            >
              <h2 class="widget-demographic-profile__cards-feelings__title">Sentimento</h2>

              <div class="widget-demographic-profile__cards-feelings__container">
                <p class="widget-demographic-profile__card-text">Não existem dados suficientes</p>
              </div>
            </button>
          </div>
        </template>
      </BaseGrid>
    </div>
  </BasePaneCard>
</template>

<script>
  import BasePaneCard from '@/components/base/BasePaneCard.vue'
  import BaseInteractiveMap from '@/components/base/BaseInteractiveMap.vue'
  import BaseGrid from '@/components/base/BaseGrid.vue'
  import BaseEmptyState from '@/components/base/BaseEmptyState.vue'
  import BaseLoaderDefault from '@/components/base/BaseLoaderDefault.vue'
  import brazilRegions from '@/assets/json/brazil-regions.json'
  import {
    dateFilter,
    percentageFilter,
    numberFilter,
    currencyFilter,
    truncateFilter
  } from '@/utils/filters'
  import { mapState, mapGetters } from 'vuex'
  import dayjs from 'dayjs'
  import axios from 'axios'

  export default {
    name: 'WidgetDemographicProfile',

    components: {
      BasePaneCard,
      BaseInteractiveMap,
      BaseGrid,
      BaseEmptyState,
      BaseLoaderDefault
    },

    filters: {
      date: dateFilter,
      percentage: percentageFilter,
      number: numberFilter,
      currency: currencyFilter,
      truncate: truncateFilter
    },

    data() {
      return {
        brazilRegions,
        dataset: [],
        selectedCard: 'conversion_rate',
        selectedRegion: 'BR',
        hoveredRegion: null,
        isLoading: false,
        cards: [
          {
            key: 'conversion_rate',
            title: 'Taxa de conversão',
            value: 'conversion_rate',
            valueType: 'percentage'
          },
          {
            key: 'average_ticket',
            title: 'Ticket médio',
            value: 'average_ticket',
            valueType: 'currency'
          },
          {
            key: 'conversions',
            title: 'Conversões',
            value: 'conversions',
            valueType: 'number',
            text: 'total_value_sold',
            textType: 'currency',
            size: 'medium'
          },
          {
            key: 'csat',
            title: 'Csat',
            value: 'csat',
            valueType: 'percentage',
            size: 'medium'
          },
          {
            key: 'best_selling_product',
            title: 'Produto mais vendido',
            text: 'best_selling_product',
            size: 'large'
          },
          {
            key: 'reason_refusal',
            title: 'Principal motivo de recusa',
            text: 'reason_refusal',
            size: 'large'
          }
        ],
        feelings: [
          {
            key: 'negative_feeling',
            label: 'Negativo',
            icon: 'SvgIconFaceSad',
            valueType: 'percentage'
          },
          {
            key: 'neutral_feeling',
            label: 'Neutro',
            icon: 'SvgIconFaceNeutral',
            valueType: 'percentage'
          },
          {
            key: 'positive_feeling',
            label: 'Positivo',
            icon: 'SvgIconFaceHappy',
            valueType: 'percentage'
          }
        ],
        disabledPlatformIds: [683]
      }
    },

    computed: {
      ...mapState('base', ['mainApi', 'pageFilter']),

      ...mapGetters('auth', ['userId', 'platformId']),

      ...mapGetters('base', ['isAppliedPageFilter']),

      indicators() {
        if (!this.dataset.length || this.dataset.error) {
          return []
        }

        return this.dataset
      },

      cardsTypes() {
        const cards = this.cards.concat(this.feelings)

        const cardsTypes = cards.reduce((types, card) => {
          types[card.key] = card.valueType
          return types
        }, {})

        return cardsTypes
      },

      formattedIndicators() {
        if (!this.indicators.length) {
          return []
        }

        return this.indicators.map(indicator => {
          const formattedIndicator = Object.assign({}, indicator)

          Object.keys(this.cardsTypes).map(key => {
            formattedIndicator[key] = this.formatIndicatorValue(
              indicator[key],
              this.cardsTypes[key]
            )
          })

          return formattedIndicator
        })
      },

      selectedRegionIndicators() {
        return this.indicators.find(indicator => indicator.region === this.selectedRegion) || {}
      },

      filteredCards() {
        if (this.disabledPlatformIds.includes(this.platformId)) {
          return this.cards.filter(card => card.key !== 'best_selling_product')
        }

        return this.cards
      },

      chips() {
        if (this.hoveredRegion) {
          return [
            this.mapRegionCodeToName(this.selectedRegion),
            this.mapRegionCodeToName(this.hoveredRegion)
          ]
        }

        return [this.mapRegionCodeToName(this.selectedRegion)]
      }
    },

    watch: {
      pageFilter() {
        this.fetchData()
      }
    },

    created() {
      this.fetchData()
    },

    methods: {
      mapRegionCodeToName(code) {
        const codes = {
          BR: 'Brasil',
          N: 'Norte',
          NE: 'Nordeste',
          CO: 'Centro-Oeste',
          SE: 'Sudeste',
          S: 'Sul'
        }

        return codes[code]
      },

      formatIndicatorValue(value, type) {
        if (value === null) {
          return
        }

        switch (type) {
          case 'number':
            return numberFilter(value)
          case 'percentage':
            return percentageFilter(value * 100)
          case 'currency':
            return currencyFilter(value)
          default:
            return value
        }
      },

      isCardActive(card) {
        return this.selectedCard === card
      },

      isIndicatorEmpty(cardKey) {
        if (cardKey === 'feeling') {
          const indicators = ['negative_feeling', 'neutral_feeling', 'positive_feeling']

          return indicators.every(indicator => this.selectedRegionIndicators[indicator] === null)
        }

        return this.selectedRegionIndicators?.[cardKey] === null
      },

      isIndicatorDisabled(cardKey) {
        return this.selectedRegionIndicators?.[cardKey] === undefined
      },

      getUrlParams() {
        let params = '?'
        let [dateStart, dateEnd] = this.pageFilter.dateRange
        const { action, plugin } = this.pageFilter

        if (!dateStart || !dateEnd) {
          dateStart = dayjs()
          dateEnd = dateStart.add(1, 'day')
        }

        params += `&firstDate=${dateFilter(dateStart, 'base')}`
        params += `&lastDate=${dateFilter(dateEnd, 'base')}`

        if (action.length) {
          params += `&actions=${action.join()}`
        }

        if (plugin) {
          params += `&plugin=${plugin}`
        }

        return params.replace(/\?$/, '').replace(/\?&/, '?')
      },

      async fetchData() {
        this.isLoading = true

        try {
          const params = this.getUrlParams()
          const endpoints = [
            `${this.mainApi}lead/demographic/region/sentiments${params}`,
            `${this.mainApi}lead/demographic/region/refusal${params}`,
            `${this.mainApi}lead/demographic/region/product${params}`,
            `${this.mainApi}lead/demographic/region/sales${params}`
          ]

          const responses = await this.fetchEndpoints(endpoints)
          this.dataset = this.joinResponses(responses)
          this.setSelectedCard();
        } catch (error) {
          this.dataset = { error }
          throw error
        } finally {
          this.isLoading = false
        }
      },

      setSelectedCard() {
        const regionIndicators = this.dataset.find(indicator => indicator.region === this.selectedRegion) || {};

        const cardKeys = this.cards.concat(this.feelings).map(card => card.key);
        const validCardKey = cardKeys.find(key => regionIndicators[key] !== null && regionIndicators[key] !== undefined && regionIndicators[key] !== 0);

        if (validCardKey == "negative_feeling" || validCardKey == "neutral_feeling" || validCardKey == "positive_feeling") {
          this.selectedCard = 'feeling';
          return;
        } else {
          this.selectedCard = validCardKey || 'conversion_rate'; 
        }
},


      async fetchEndpoints(endpoints) {
        const requests = endpoints.map(endpoint => axios.get(endpoint))
        return await Promise.allSettled(requests)
      },

      joinResponses(responses) {
        const joinedData = responses
          .flatMap(response => {
            if (response.status === 'fulfilled') {
              return response.value.data.data
            }

            return []
          })
          .reduce((acc, data) => {
            const existing = acc.find(item => item.region === data.region)
            if (existing) {
              Object.assign(existing, data)
            } else {
              acc.push(data)
            }
            return acc
          }, [])

        return joinedData
      }
    }
  }
</script>

<style lang="scss" scoped>
  .widget-demographic-profile {
    min-height: 500px;

    &__map {
      align-self: center;
    }

    &__cards {
      display: flex;
      flex-direction: column;
      gap: 16px;

      &-indicators {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }

      &-feelings {
        all: unset;
        padding: 24px;

        &__title {
          font: {
            size: 16px;
            weight: 700;
          }
          line-height: 20px;
          margin-bottom: 14px;
        }

        &__container {
          display: flex;
          width: 100%;
          gap: 100px;
        }
      }
    }

    &__card {
      all: unset;
      cursor: pointer;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      text-align: start;
      padding: 24px;
      border: 2px solid transparent;
      border-radius: 24px;
      height: 116px;
      color: #172542;

      &-value {
        margin-top: 8px;
      }

      &-text {
        font: {
          size: 12px;
          weight: 400;
        }
        line-height: 16px;
        margin-top: 4px;
      }

      &-title,
      &-value {
        font: {
          size: 16px;
          weight: 700;
        }
        line-height: 20px;
      }

      &--active {
        background-color: rgba(236, 236, 226, 0.3);
        border: 2px solid #98c1dd;
      }

      &--disabled {
        color: #8e8e8e;
      }

      &--horizontal {
        height: auto;
      }

      &:hover:not(&--active) {
        border: 2px solid #c7c7c7;
      }
    }

    &__feeling {
      display: flex;
      flex-direction: column;
      align-items: center;

      svg {
        width: 32px;
        height: 32px;
      }
    }

    &__loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 500px;
    }

    &__error-state {
      display: flex;
      place-items: center;
      min-height: 500px;
      grid-column: span 2 / span 2;
    }

    &__chips {
      display: flex;
      gap: 8px;

      .chip {
        padding: 6px;
        background: rgba(226, 235, 240, 0.5);
        border-radius: 6px;
        color: var(--Blue, #172542);
        line-height: 20px;
        font-weight: 700;
      }
    }
  }
</style>
